/**
 * ProjectCreateModal — Phase 2b create-project flow.
 *
 * Opened from the home-page empty state. Submitting POSTs `/api/projects`
 * via `useCreateProject`, then navigates the caller into the new
 * project's board. Server validation errors are surfaced inline (under
 * the relevant field if we can map them, otherwise at form level); a
 * success toast confirms the create before the navigation lands.
 *
 * Form rules:
 *   - `name` is required (BE Zod schema: min 1, max 255).
 *   - `description` is optional (defaults to '' on the BE).
 *   - Enter submits; ESC closes via the Modal's built-in handler.
 *   - The Modal auto-focuses the first focusable element on open, which
 *     is the name input — so no explicit `autoFocus` needed.
 */

import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { localizeError } from '@/api/error-codes'
import { Button } from '@/components/Button'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { TextArea } from '@/components/TextArea'
import { useToast } from '@/components/ToastProvider'
import { ClientPicker } from '@/features/clients/ClientPicker'
import { useCreateProject } from '@/features/projects/use-create-project'

interface ProjectCreateModalProps {
  open: boolean
  onClose: () => void
}

export function ProjectCreateModal({ open, onClose }: ProjectCreateModalProps) {
  const { t } = useTranslation(['common', 'validation'])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [clientId, setClientId] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const navigate = useNavigate()
  const toast = useToast()
  const createProject = useCreateProject()

  const reset = () => {
    setName('')
    setDescription('')
    setClientId(null)
    setNameError(null)
    setClientError(null)
    setFormError(null)
  }

  const handleClose = () => {
    if (createProject.isPending) return
    reset()
    onClose()
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setNameError(null)
    setClientError(null)
    setFormError(null)

    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError(t('validation:projectNameRequired'))
      return
    }

    if (!clientId) {
      setClientError(
        t('validation:projectClientRequired', { defaultValue: 'Pick a client for this project.' }),
      )
      return
    }

    try {
      const project = await createProject.mutateAsync({
        name: trimmedName,
        description: description.trim() || undefined,
        clientId,
      })
      toast.success(t('projects.create.successToast'))
      reset()
      onClose()
      navigate({ to: '/board/$projectId', params: { projectId: project.id } })
    } catch (err) {
      // `localizeError` maps the wire `code` to the catalog and only
      // falls back to the BE's English `message`. Rendering `err.message`
      // directly skips the catalog entirely, which is how a guest's
      // `guest_forbidden` refusal surfaced as the BE's dev-fallback
      // string instead of the localized copy.
      const message =
        err instanceof Error ? localizeError(err, t) : t('projects.create.errorFallback')
      setFormError(message)
      toast.error(message)
    }
  }

  return (
    <Modal open={open} onClose={handleClose} ariaLabel={t('projects.create.ariaLabel')}>
      <form onSubmit={handleSubmit} noValidate>
        <Modal.Header>{t('projects.create.title')}</Modal.Header>
        <Modal.Body className="flex flex-col gap-4">
          <FormField
            id="project-create-name"
            label={t('projects.create.nameLabel')}
            placeholder={t('projects.create.namePlaceholder')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={nameError ?? undefined}
            required
            maxLength={255}
            disabled={createProject.isPending}
          />
          <TextArea
            id="project-create-description"
            label={t('projects.create.descriptionLabel')}
            placeholder={t('projects.create.descriptionPlaceholder')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={createProject.isPending}
          />
          <ClientPicker
            id="project-create-client"
            label={t('projects.create.clientLabel', { defaultValue: 'Client' })}
            value={clientId}
            onChange={(next) => {
              setClientId(next)
              if (next) setClientError(null)
            }}
            disabled={createProject.isPending}
            required
            error={clientError}
            hint={t('projects.create.clientHint', {
              defaultValue:
                "Pick a client to bill against. Use Internal for your own organization's work.",
            })}
          />
          {formError && (
            <p role="alert" className="text-xs text-danger">
              {formError}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={createProject.isPending}
          >
            {t('actions.cancel')}
          </Button>
          <Button type="submit" disabled={createProject.isPending}>
            {createProject.isPending
              ? t('projects.create.submitPending')
              : t('projects.create.submitIdle')}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}
